// src/pages/LegalEntityPage.jsx

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    getLegalEntity,
    createLegalEntity,
    updateLegalEntity,
    deleteLegalEntity,
    getLegalEntityDeliveryPrice,
} from "../api/legalEntityService";
import api from "../api/api";

// ============ HELPERS ============
function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

function Field({ label, name, value, onChange, required }) {
    return (
        <div>
            <label className="block text-sm font-medium">
                {label} {required && <span className="text-red-600">*</span>}
            </label>
            <input
                name={name}
                value={value}
                onChange={onChange}
                className="mt-1 w-full border rounded px-3 py-2"
            />
        </div>
    );
}

const nullIfEmpty = (v) => (String(v ?? "").trim() === "" ? null : v);

const initialForm = {
    loginId: 0,
    companyName: "",
    taxId: "",
    registrationNumber: "",
    contactPerson: "",
    contactPosition: "",
    companyPhone: "",
    manualCountry: "",
    manualCity: "",
    manualPostalCode: "",
    manualAddress: "",
};

// ==========================
//       MAIN PAGE
// ==========================

export default function LegalEntityPage() {
    const query = useQuery();
    const entityIdFromUrl = query.get("id");

    const [mode, setMode] = useState(entityIdFromUrl ? "update" : "create");
    const [entityId, setEntityId] = useState(entityIdFromUrl ? Number(entityIdFromUrl) : null);

    const [me, setMe] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState("");
    const [deliveryInfo, setDeliveryInfo] = useState(null);

    // ==========================
    //   LOAD CURRENT USER
    // ==========================
    useEffect(() => {
        let aborted = false;

        (async () => {
            try {
                const res = await api.get("/login/me");
                if (aborted) return;

                setMe(res.data);
                setForm((f) => ({ ...f, loginId: res.data.id }));
            } catch (e) {
                if (!aborted) setErr("Failed to load user profile");
            }
        })();

        return () => {
            aborted = true;
        };
    }, []);

    // ==========================
    //   LOAD ENTITY FOR UPDATE
    // ==========================
    useEffect(() => {
        if (!entityId) return;

        let aborted = false;

        (async () => {
            try {
                const data = await getLegalEntity(entityId);
                if (aborted) return;

                setForm({
                    loginId: data.loginId,
                    companyName: data.companyName || "",
                    taxId: data.taxId || "",
                    registrationNumber: data.registrationNumber || "",
                    contactPerson: data.contactPerson || "",
                    contactPosition: data.contactPosition || "",
                    companyPhone: data.companyPhone || "",
                    manualCountry: data.manualCountry || "",
                    manualCity: data.manualCity || "",
                    manualPostalCode: data.manualPostalCode || "",
                    manualAddress: data.manualAddress || "",
                });

                setMode("update");
            } catch (e) {
                if (!aborted) setErr("Failed to load legal entity");
            }
        })();

        return () => {
            aborted = true;
        };
    }, [entityId]);

    // ==========================
    //   FORM LOGIC
    // ==========================
    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };
    const validate = () => {
        const required = [
            "companyName",
            "taxId",
            "registrationNumber",
            "contactPerson",
            "contactPosition",
            "companyPhone",
            "manualCountry",
            "manualCity",
            "manualPostalCode",
            "manualAddress",
        ];

        const missing = required.filter(
            (k) => !String(form[k] ?? "").trim()
        );

        if (missing.length)
            return `Please fill: ${missing.join(", ")}`;

        if (mode === "create" && !form.loginId) {
            return "User not loaded (loginId missing).";
        }

        return null;
    };

    // ==========================
    //       SUBMIT FORM
    // ==========================
    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setOk("");

        const v = validate();
        if (v) {
            setErr(v);
            return;
        }

        setBusy(true);
        try {
            if (mode === "update" && entityId) {
                // ---------- UPDATE ----------
                const payload = {
                    id: entityId,
                    companyName: form.companyName,
                    taxId: nullIfEmpty(form.taxId),
                    registrationNumber: nullIfEmpty(form.registrationNumber),
                    contactPerson: nullIfEmpty(form.contactPerson),
                    contactPosition: nullIfEmpty(form.contactPosition),
                    companyPhone: nullIfEmpty(form.companyPhone),
                    manualCountry: nullIfEmpty(form.manualCountry),
                    manualCity: nullIfEmpty(form.manualCity),
                    manualPostalCode: nullIfEmpty(form.manualPostalCode),
                    manualAddress: nullIfEmpty(form.manualAddress),
                };

                await updateLegalEntity(payload); // <-- FIXED
                setOk("Legal entity updated.");
            } else {
                // ---------- CREATE ----------
                const payload = {
                    loginId: form.loginId,
                    companyName: form.companyName,
                    taxId: nullIfEmpty(form.taxId),
                    registrationNumber: nullIfEmpty(form.registrationNumber),
                    contactPerson: nullIfEmpty(form.contactPerson),
                    contactPosition: nullIfEmpty(form.contactPosition),
                    companyPhone: nullIfEmpty(form.companyPhone),
                    manualCountry: form.manualCountry,
                    manualCity: form.manualCity,
                    manualPostalCode: form.manualPostalCode,
                    manualAddress: form.manualAddress,
                };

                const created = await createLegalEntity(payload);
                setOk("Legal entity created.");

                if (created?.id) {
                    setEntityId(created.id);
                    setMode("update");
                }
            }
        } catch (e) {
            setErr(e?.response?.data?.message || e.message || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    // ==========================
    //       DELETE ENTITY
    // ==========================
    const onDelete = async () => {
        if (!entityId) return;
        if (!confirm("Delete this legal entity permanently?")) return;

        try {
            await deleteLegalEntity(entityId);

            setOk("Legal entity deleted.");
            setMode("create");
            setEntityId(null);
            setForm({
                ...initialForm,
                loginId: me?.id ?? 0,
            });
            setDeliveryInfo(null);
        } catch (e) {
            setErr("Delete failed: " + (e.message || "Unknown error"));
        }
    };

    // ==========================
    //   DELIVERY PRICE
    // ==========================
    const loadDeliveryInfo = async () => {
        if (!entityId) {
            setErr("Save entity first.");
            return;
        }

        try {
            const info = await getLegalEntityDeliveryPrice(entityId);
            setDeliveryInfo(info);
        } catch (e) {
            setErr("Failed to load delivery info");
        }
    };

    // ==========================
    //         RENDER
    // ==========================

    return (
        <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow">
            <h2 className="text-2xl font-bold mb-4">
                {mode === "update" ? "Edit Legal Entity" : "Create Legal Entity"}
            </h2>

            {me && (
                <p className="text-sm text-gray-600 mb-4">
                    Logged in as: <strong>{me.email}</strong> (UserId: {me.id})
                </p>
            )}

            {err && <div className="mb-3 text-red-600 text-sm">{err}</div>}
            {ok && <div className="mb-3 text-green-600 text-sm">{ok}</div>}

            <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
                {mode === "create" && (
                    <Field label="Login ID" name="loginId" value={form.loginId} onChange={onChange} required />
                )}

                <Field label="Company name" name="companyName" value={form.companyName} onChange={onChange} required />
                <Field label="Tax ID" name="taxId" value={form.taxId} onChange={onChange} />
                <Field label="Registration number" name="registrationNumber" value={form.registrationNumber} onChange={onChange} />
                <Field label="Contact person" name="contactPerson" value={form.contactPerson} onChange={onChange} />
                <Field label="Contact position" name="contactPosition" value={form.contactPosition} onChange={onChange} />
                <Field label="Company phone" name="companyPhone" value={form.companyPhone} onChange={onChange} />

                <div className="pt-2">
                    <h3 className="font-semibold mb-2">Manual Address</h3>
                    <Field required label="Country" name="manualCountry" value={form.manualCountry} onChange={onChange} />
                    <Field required label="City" name="manualCity" value={form.manualCity} onChange={onChange} />
                    <Field required label="Postal code" name="manualPostalCode" value={form.manualPostalCode} onChange={onChange} />
                    <Field required label="Address" name="manualAddress" value={form.manualAddress} onChange={onChange} />
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                        disabled={busy}
                    >
                        {busy ? "Saving..." : mode === "update" ? "Save changes" : "Create"}
                    </button>

                    <button
                        type="button"
                        className="px-4 py-2 rounded border"
                        onClick={() => {
                            setForm({
                                ...initialForm,
                                loginId: me?.id ?? 0,
                            });
                            setErr("");
                            setOk("");
                        }}
                    >
                        Reset
                    </button>

                    <button
                        type="button"
                        className="px-4 py-2 rounded border"
                        onClick={loadDeliveryInfo}
                        disabled={!entityId}
                    >
                        Load delivery info
                    </button>

                    {mode === "update" && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            onClick={onDelete}
                        >
                            Delete
                        </button>
                    )}
                </div>
            </form>

            {deliveryInfo && (
                <div className="mt-6 p-3 bg-gray-50 rounded border text-sm">
                    <div className="font-semibold mb-1">Delivery info</div>
                    <pre className="whitespace-pre-wrap text-xs">
                        {JSON.stringify(deliveryInfo, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}
